import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Download, X } from 'lucide-react';
import { closeInstallPrompt, isIos, promptNativeInstall } from '@/lib/pwa';
import { useToast } from '@/components/ui/Toast';
import { usePwaInstall } from './usePwaInstall';

function ShareIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3v12" />
      <path d="m8 7 4-4 4 4" />
      <path d="M6 11H5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-8a1 1 0 0 0-1-1h-1" />
    </svg>
  );
}

function AddToHomeIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3.5" y="3.5" width="17" height="17" rx="4" />
      <path d="M12 8.5v7M8.5 12h7" />
    </svg>
  );
}

function ConfirmAddIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m5 12.5 4.5 4.5L19 7.5" />
    </svg>
  );
}

const IOS_STEPS = [
  { icon: ShareIcon, title: 'Toque em Compartilhar', detail: 'O ícone fica na barra do Safari, embaixo (iPhone) ou em cima (iPad).' },
  { icon: AddToHomeIcon, title: 'Escolha "Adicionar à Tela de Início"', detail: 'Role a lista de opções se não aparecer de imediato.' },
  { icon: ConfirmAddIcon, title: 'Toque em "Adicionar"', detail: 'O PatrociConnect aparece junto dos seus outros apps.' },
];

export function InstallPrompt() {
  const { isOpen, canPromptNatively } = usePwaInstall();
  const toast = useToast();
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);
  const [isInstalling, setIsInstalling] = useState(false);
  const shouldShowIosSteps = !canPromptNatively && isIos();

  useEffect(() => {
    if (!isOpen) return;
    previousFocus.current = document.activeElement as HTMLElement | null;
    const focusTimer = setTimeout(() => {
      dialogRef.current?.querySelector<HTMLElement>('[data-autofocus]')?.focus();
    }, 50);

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        closeInstallPrompt({ declined: true });
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusable = dialogRef.current.querySelectorAll<HTMLElement>('button:not([disabled]), [href]');
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      clearTimeout(focusTimer);
      document.removeEventListener('keydown', handleKeyDown);
      previousFocus.current?.focus?.();
    };
  }, [isOpen]);

  async function handleInstall() {
    setIsInstalling(true);
    try {
      const isAccepted = await promptNativeInstall();
      closeInstallPrompt({ declined: false });
      if (isAccepted) toast.success('O PatrociConnect está sendo instalado neste aparelho.', 'Instalação iniciada');
    } catch (error) {
      console.error('[pwa] falha ao abrir a instalação nativa', error);
      toast.error('Não foi possível abrir a instalação agora. Tente pelo menu do navegador.', undefined, { action: null });
      closeInstallPrompt({ declined: false });
    } finally {
      setIsInstalling(false);
    }
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[70] flex items-end justify-center p-4 sm:items-center">
          <motion.div
            className="absolute inset-0 bg-ink/40 backdrop-blur-[2px]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => closeInstallPrompt({ declined: true })}
            aria-hidden="true"
          />
          <motion.div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="pwa-install-title"
            aria-describedby="pwa-install-desc"
            className="relative w-full max-w-md overflow-hidden rounded-3xl border border-line bg-elevated shadow-elevation-3"
            initial={{ opacity: 0, y: 32, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ type: 'spring', stiffness: 380, damping: 32 }}
          >
            {/* Faixa da marca com o ícone do app "pousando" */}
            <div className="relative h-28 overflow-hidden bg-brand-500">
              <div className="absolute -right-10 -top-16 h-48 w-48 rounded-full bg-accent/25 blur-2xl" aria-hidden="true" />
              <div className="absolute -bottom-20 left-6 h-40 w-40 rounded-full bg-white/10" aria-hidden="true" />
              <motion.img
                src="/pwa-192x192.png"
                alt=""
                className="absolute bottom-3 left-6 h-16 w-16 rounded-2xl shadow-glow-brand-lg ring-4 ring-white/20"
                initial={{ y: -30, rotate: -8, opacity: 0 }}
                animate={{ y: 0, rotate: 0, opacity: 1 }}
                transition={{ delay: 0.12, type: 'spring', stiffness: 260, damping: 18 }}
              />
              <button
                type="button"
                onClick={() => closeInstallPrompt({ declined: true })}
                className="absolute right-3 top-3 rounded-full p-2 text-on-brand/80 transition-colors hover:bg-white/15 hover:text-on-brand focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
                aria-label="Fechar e não instalar agora"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>

            <div className="px-6 pb-6 pt-5">
              <h2 id="pwa-install-title" className="font-display text-xl font-bold tracking-tight text-ink">
                Leve o PatrociConnect no bolso
              </h2>
              <p id="pwa-install-desc" className="mt-2 text-sm leading-relaxed text-muted">
                {shouldShowIosSteps
                  ? 'No iPhone e no iPad a instalação é feita pelo Safari, em três toques:'
                  : 'Instale o app para abrir direto da tela inicial e acompanhar propostas e negociações mais rápido.'}
              </p>

              {shouldShowIosSteps && (
                <ol className="mt-5 space-y-3">
                  {IOS_STEPS.map((step, index) => (
                    <motion.li
                      key={step.title}
                      className="flex items-start gap-3 rounded-2xl border border-line bg-canvas p-3"
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.15 + index * 0.08 }}
                    >
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-brand-500/10 text-brand-500">
                        <step.icon />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold text-ink">
                          <span className="mr-1 text-muted">{index + 1}.</span>
                          {step.title}
                        </span>
                        <span className="mt-0.5 block text-xs leading-relaxed text-muted">{step.detail}</span>
                      </span>
                    </motion.li>
                  ))}
                </ol>
              )}

              <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => closeInstallPrompt({ declined: true })}
                  data-autofocus={shouldShowIosSteps ? undefined : true}
                >
                  Agora não
                </button>
                {shouldShowIosSteps ? (
                  <button type="button" className="btn-primary" onClick={() => closeInstallPrompt({ declined: false })} data-autofocus>
                    Entendi
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn-primary inline-flex items-center justify-center gap-2"
                    onClick={handleInstall}
                    disabled={isInstalling}
                  >
                    <Download className="h-4 w-4" aria-hidden="true" />
                    {isInstalling ? 'Abrindo...' : 'Instalar'}
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
